import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setValid(!!data.valid))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset password. Please request a new link.");
      // invalid/expired/used → surface the friendly invalid-link screen
      if (err.response?.status === 400) setValid(false);
    } finally {
      setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex mb-8"><Logo height={30} /></Link>
        {children}
      </div>
    </div>
  );

  if (checking) {
    return <Shell><div className="text-[#5C5856] inline-flex items-center gap-2" data-testid="reset-checking"><Loader2 className="w-4 h-4 animate-spin" /> Checking your link…</div></Shell>;
  }

  if (done) {
    return (
      <Shell>
        <div data-testid="reset-success">
          <div className="w-12 h-12 rounded-full bg-[#E7EFE6] flex items-center justify-center mb-4"><CheckCircle2 className="w-6 h-6 text-[#4F6A4C]" /></div>
          <h1 className="text-3xl font-bold tracking-tight text-[#2A2626]">Password updated</h1>
          <p className="mt-2 text-[#5C5856]">Your password has been reset. You can now sign in with your new password.</p>
          <Button onClick={() => navigate("/login", { replace: true })} className="mt-8 h-11 bg-[#2A2626] hover:bg-black text-white rounded-full px-6" data-testid="reset-go-login">Go to sign in</Button>
        </div>
      </Shell>
    );
  }

  if (!valid) {
    return (
      <Shell>
        <div data-testid="reset-invalid">
          <div className="w-12 h-12 rounded-full bg-[#F6E2DE] flex items-center justify-center mb-4"><AlertTriangle className="w-6 h-6 text-[#8A3B2F]" /></div>
          <h1 className="text-3xl font-bold tracking-tight text-[#2A2626]">Link expired or invalid</h1>
          <p className="mt-2 text-[#5C5856]">This password reset link is no longer valid. Reset links expire after 30 minutes and can only be used once.</p>
          <Link to="/forgot-password"><Button className="mt-8 h-11 bg-[#2A2626] hover:bg-black text-white rounded-full px-6" data-testid="reset-request-new">Request a new link</Button></Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-3xl font-bold tracking-tight text-[#2A2626]">Set a new password</h1>
      <p className="mt-2 text-[#5C5856]">Choose a new password for your MinistryHub account.</p>
      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" data-testid="reset-password" />
          <p className="text-[11px] text-[#8A857F] mt-1">At least 6 characters.</p>
        </div>
        <div>
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-11" data-testid="reset-confirm" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-11 bg-[#2A2626] hover:bg-black text-white rounded-full" data-testid="reset-submit">
          {busy ? "Updating…" : "Reset password"}
        </Button>
      </form>
    </Shell>
  );
}
